import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { NAV_MENUS } from "../data/site";
import { slugOf } from "../components/Navbar";
import { Reveal } from "../components/Reveal";

const HEADERS = {
  solutions: { title: "Solutions", sub: "One ecosystem, multiple technologies — five solution pillars engineered to interlock." },
  services: { title: "Services", sub: "From advisory and design to implementation, integration and lifecycle support." },
  industries: { title: "Industries", sub: "Deep context in every sector — infrastructure designed for your reality." },
  resources: { title: "Resource Hub", sub: "News, events, insights and assets from the SMIPL ecosystem." },
  partners: { title: "Technology Ecosystem", sub: "Eight partner domains with certified OEM ecosystems behind every engagement." },
  "about-us": { title: "About SMIPL", sub: "The company, the approach and the people behind the commitment." },
};

const GroupPage = () => {
  const { group } = useParams();
  const menu = NAV_MENUS.find((m) => slugOf(m.label) === group);
  if (!menu) return <Navigate to="/" replace />;
  const header = HEADERS[group] || { title: menu.label, sub: "" };

  return (
    <main className="pt-40 pb-28 min-h-screen" data-testid={`group-page-${group}`}>
      <div className="max-w-6xl mx-auto px-6">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-smipl-orange mb-4">{menu.label}</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">{header.title}</h1>
          {header.sub && <p className="mt-6 text-base md:text-lg text-slate-400 max-w-2xl leading-relaxed">{header.sub}</p>}
        </Reveal>
        <div className={`mt-14 grid gap-5 ${menu.items.length > 4 ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2"}`}>
          {menu.items.map((item, i) =>
            item.scroll ? (
              <Link
                key={item.id}
                to="/"
                data-testid={`group-card-${item.id}`}
                className="tracing-border group rounded-2xl border border-white/8 bg-navy-deep/60 p-7 block transition-all duration-300 hover:-translate-y-1.5 hover:border-smipl-orange/40"
              >
                <item.icon size={22} className="text-smipl-orange" />
                <h3 className="mt-5 font-display text-lg font-bold flex items-center justify-between gap-3">
                  {item.name}
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-smipl-orange/40 text-smipl-orange transition-all duration-300 group-hover:bg-smipl-orange group-hover:text-white group-hover:scale-110">
                    <ArrowUpRight size={13} />
                  </span>
                </h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{item.blurb}</p>
                <p className="mt-4 font-mono text-[10px] tracking-[0.25em] uppercase text-smipl-orange/60 group-hover:text-smipl-orange transition-colors">
                  Open detailed page
                </p>
              </Link>
            ) : (
              <Link
                key={item.id}
                to={`/${group}/${item.id}`}
                data-testid={`group-card-${item.id}`}
                className="tracing-border group rounded-2xl border border-white/8 bg-navy-deep/60 p-7 block transition-all duration-300 hover:-translate-y-1.5 hover:border-smipl-orange/40"
              >
                <item.icon size={22} className="text-smipl-orange" />
                <h3 className="mt-5 font-display text-lg font-bold flex items-center justify-between gap-3">
                  {item.name}
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-smipl-orange/40 text-smipl-orange transition-all duration-300 group-hover:bg-smipl-orange group-hover:text-white group-hover:scale-110">
                    <ArrowUpRight size={13} />
                  </span>
                </h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{item.blurb}</p>
                <p className="mt-4 font-mono text-[10px] tracking-[0.25em] uppercase text-smipl-orange/60 group-hover:text-smipl-orange transition-colors">
                  Open detailed page
                </p>
                {item.partners?.length > 0 && (
                  <p className="mt-4 font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">
                    {item.partners.length} ecosystem partner{item.partners.length > 1 ? "s" : ""}
                  </p>
                )}
                {item.note && (
                  <span className="mt-4 inline-block rounded-full border border-smipl-orange/30 bg-smipl-orange/10 px-3 py-1 font-mono text-[10px] tracking-[0.2em] uppercase text-smipl-orange">
                    {item.note}
                  </span>
                )}
              </Link>
            )
          )}
        </div>
      </div>
    </main>
  );
};

export default GroupPage;
